import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../components/toast/useToast';
import { isMockActive } from '../../../utils/isMockActive';
import {
    getKanalJednotky,
    getHistorieZprav,
    posliZpravu,
    KanalJednotkyResponse,
    HistorieStranka,
} from './ChatService';

/**
 * HO-147 Task 4: React Query hooky nad chatem kanalu jednotky.
 *
 * HO-152: dotazovani uz NENI jedina cesta k novym zpravam - `useChatSocket`
 * zneplatnuje tyhle klice na serverovy ramec `ZMENA`. Dotazovani ale zustava,
 * a to ve dvou rychlostech: bez socketu je jedinym zdrojem novych zprav
 * (proxy bez `Upgrade`, vypadek), se socketem je uz jen zachrannou siti pro
 * pripad tise mrtveho spojeni. Proto je interval parametr, ne konstanta.
 */

const KANAL_JEDNOTKY_POLL_MS = 15000;
const ZPRAVY_POLL_MS = 5000;

/** Se zivym socketem staci pomale kolo - novinky chodi ramcem ZMENA. */
export const ZPRAVY_POLL_SE_SOCKETEM_MS = 60000;

/**
 * Kanal jednotky - metadata (zamek, ztlumeni, nahled posledni zpravy). Cteni
 * NENI gatovane feature flagem (BE ho zamerne negatuje, viz javadoc
 * `ChatController#kanalJednotky`) - vola se vzdy pro prihlaseneho uzivatele,
 * protoze i s vypnutym MODUL_CHAT musi menu vedet, jestli uz existuje
 * historie (viz `isChatMenuVisible` v AdminLayoutu).
 */
/**
 * Kanal jednotky (metadata + nahled posledni zpravy).
 *
 * POZOR, tenhle GET NENI ctecí: backend na nem vola
 * `ziskejNeboZalozKanalJednotky()`, tedy insert-or-get. Kazde zavolani u jednotky,
 * ktera kanal jeste nema, ho ZALOZI. Proto oba parametry:
 *
 * - `enabled` — `AdminLayout` ho drive volal NEPODMINENE na kazde admin strance,
 *   takze pouhe otevreni libovolne stranky zakladalo konverzaci i u jednotek,
 *   ktere chat nikdy nezaply. Zapisovy vedlejsi ucinek zavesenny na vykresleni
 *   shellu. Kdyz je `MODUL_CHAT` zapnuty, menu odpoved vubec nepotrebuje.
 * - `pollovat` — 15s poll ma smysl na stranke chatu, ne v layoutu. Drive to byly
 *   ctyri pozadavky za minutu z KAZDE otevrene admin zalozky.
 */
export const useKanalJednotky = (enabled = true, pollovat = true) => {
    return useQuery<KanalJednotkyResponse, Error>({
        queryKey: ['chat', 'kanalJednotky'],
        queryFn: () => getKanalJednotky(),
        enabled: enabled && !isMockActive(),
        refetchInterval: pollovat ? KANAL_JEDNOTKY_POLL_MS : false,
        refetchOnWindowFocus: false,
        retry: false,
    });
};

/** Historie zprav dane konverzace - vypnuta, dokud neni znamo `konverzaceId`. */
export const useHistorieZprav = (konverzaceId: number | undefined, pollMs: number = ZPRAVY_POLL_MS) => {
    return useQuery<HistorieStranka, Error>({
        queryKey: ['chat', 'zpravy', konverzaceId],
        queryFn: () => getHistorieZprav(konverzaceId as number),
        enabled: !!konverzaceId && !isMockActive(),
        refetchInterval: pollMs,
        refetchOnWindowFocus: false,
        retry: false,
    });
};

/** Export JEN pro testy - generovani UUID je jinak interni detail teto sluzby. */
export const __noveUuidProTesty = (): string => noveUuid();

/**
 * Klientske UUID zpravy - serverovy dedup klic.
 *
 * <p>MUSI to byt skutecny tvar UUID. Backend ho overuje regexem
 * ({@code ZpravaValidator.UUID_TVAR}) a cokoli jineho odmita 400 "Identifikator
 * zpravy musi byt UUID".</p>
 *
 * <p>Puvodni fallback vracel `${Date.now()}-${Math.random()...}`, coz tim regexem
 * NEPROJDE NIKDY - a bezel prave tam, kde byl potreba: `crypto.randomUUID` existuje
 * jen v ZABEZPECENEM kontextu (https nebo localhost) a az od novejsich prohlizecu.
 * Velitel, ktery si dashboard otevre pres `http://192.168.x.x` na LAN ve zbrojnici
 * (TLS terminuje az proxy na jinem stroji), tak nemohl odeslat zpravu VUBEC - kazdy
 * pokus skoncil 400.
 *
 * <p>`crypto.getRandomValues` na rozdil od `randomUUID` funguje i v nezabezpecenem
 * kontextu, takze se jim UUID v4 slozi rucne. `Math.random` se zamerne nepouziva ani
 * jako posledni zaloha - je to dedup klic, u ktereho na kvalite nahodnosti zalezi.</p>
 */
const noveUuid = (): string => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    const bajty = new Uint8Array(16);
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(bajty);
    } else {
        // Posledni zaloha pro prostredi uplne bez Web Crypto. Nahodnost je slabsi, ale
        // TVAR je platny - a to je to, na cem tady zalezi: pri padu tohohle vyrazu by
        // hasic nemohl odeslat zpravu vubec, coz je horsi nez slabsi dedup klic.
        for (let i = 0; i < bajty.length; i++) {
            bajty[i] = Math.floor(Math.random() * 256);
        }
    }
    bajty[6] = (bajty[6] & 0x0f) | 0x40;   // verze 4
    bajty[8] = (bajty[8] & 0x3f) | 0x80;   // varianta RFC 4122
    const hex = Array.from(bajty, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-`
        + `${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Odeslani textove zpravy do kanalu jednotky. `konverzaceId` chodi az z
 * `useKanalJednotky` (insert-or-get), takze mutace bez nej odmita rovnou -
 * ChatPage tlacitko v tomhle stavu ani nezobrazi (`ZpravaVstup` se renderuje
 * jen po nacteni kanalu).
 */
export const useOdeslatZpravuMutation = (konverzaceId: number | undefined) => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation<void, Error, string>({
        mutationFn: async (text: string) => {
            if (!konverzaceId) {
                throw new Error('Konverzace jednotky zatím není založena.');
            }
            await posliZpravu(konverzaceId, text, noveUuid());
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chat', 'zpravy', konverzaceId] });
            queryClient.invalidateQueries({ queryKey: ['chat', 'kanalJednotky'] });
        },
        onError: (error) => {
            showToast(error.message || 'Zprávu se nepodařilo odeslat.', 'error');
        },
    });
};
